import React, { useEffect, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ErrorForm from './ErrorForm';
import { validateFormDataAction, prepareFormDataIfValidAction, setFormDataErrors, initFormAction, resetFormDataValues } from '../actions/formActions';
import { IFormMeta, IFormSettings, hasFormStep, IValidationError, FormStatus } from '../types';
import { formKeySelector } from '../selectors/formBuilder.selector';
import { getFormChildren } from '../helpers/getFormChildren';
import { scrollToRootOrError } from '../helpers/allHelpers';

interface FormBuilderProps {
    formSchema: IFormSettings;
    apiError: IValidationError | undefined;
    showSubmitButton?: boolean;
    showErrorsInForm?: boolean;
    onSubmit?(values: any): void;
}

const FormBuilder: FC<FormBuilderProps> = ({
    formSchema,
    apiError,
    onSubmit,
    showSubmitButton = true,
    showErrorsInForm = true,
}) => {
    const { formName } = formSchema.formMeta;
    const dispatch = useDispatch();
    const errors = useSelector(formKeySelector(formName, 'errors'));
    const formStatus = useSelector(formKeySelector(formName, 'formStatus'));
    const { controls = [], formMeta = {} as IFormMeta } = useSelector(formKeySelector(formName, 'initialSettings')) || {};
    const valuesToSubmit = useSelector(formKeySelector(formName, 'valuesToSubmit'));
    const hasSubmitActions = Boolean(formMeta.actions?.length);
    const doShowSubmitButton = showSubmitButton && !hasFormStep(formSchema) && !hasSubmitActions;
    const showErrorSummary = formMeta.validationErrorList !== undefined ? formMeta.validationErrorList : showErrorsInForm;

    useEffect(() => {
        dispatch(initFormAction(formSchema));

        return () => {
            dispatch(resetFormDataValues(formName));
        };
    }, [formSchema, dispatch, formName]);

    useEffect(() => {
        if (apiError) dispatch(setFormDataErrors({ ...apiError, formName }));
    }, [apiError, dispatch, formName]);

    useEffect(() => {
        if (formStatus === FormStatus.readyToSubmit && onSubmit && !hasSubmitActions) {
            onSubmit(valuesToSubmit);
            dispatch(resetFormDataValues(formName));
        }
    }, [dispatch, formName, formStatus, onSubmit, valuesToSubmit, hasSubmitActions]);

    if (!controls.length) return null;

    const onSubmitHandler = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        scrollToRootOrError(e);
        dispatch(validateFormDataAction(formName));
        dispatch(prepareFormDataIfValidAction(formName));
    };

    const formElements = getFormChildren(controls, formName);

    return (
        <form action="" id="main-form" onSubmit={onSubmitHandler}>
            {formElements}
            {Boolean(showErrorSummary && errors && errors.length) && (
                <ErrorForm
                    validationSectionTitle={formMeta.validationSectionTitle}
                    errors={errors || []}
                />
            )}
            {doShowSubmitButton && (
                <input
                    type="submit"
                    className="btn btn-default"
                    value={formMeta.submitButtonText}
                />
            )}
        </form>
    );
};

export default FormBuilder;
